// types_session_upstream.ts — the session domain's wire types, from the
// session cut-over on served by tricorder-session through the gateway's
// /sessions/** proxy (tricorder-session SPEC.md sections 2, 3, 7). They
// replace the gateway's old SessionResponse/MessageResponse shapes: an
// episode with a pinned agent, a seq-ordered record log, and provenance on
// every record — no card projections, no per-message summaries, no images.

import type { z } from 'zod';
import type { CitationResponse, Page } from './types';
import { TurnFailurePayloadSchema, type TURN_FAILURE_KINDS } from './schemas_session';

/** Who wrote a record, and on whose behalf (care-app/ADR-003). */
export type ActorRef = {
  performer: string;
  on_behalf_of?: string | null;
};

/** The agent a session pins: tricorder-agent's slug and version. */
export type PinnedAgentRef = {
  slug: string;
  version: string;
};

/** The party a session is about, as a citation (never resolved by the session service). */
export type SubjectRef = {
  type: string;
  id: string;
};

export type SessionResponse = {
  id: string;
  /** Absent on a session that pins no agent: a protocol run
   *  (tricorder-session/ADR-0009). */
  agent?: PinnedAgentRef;
  subject?: SubjectRef | null;
  /** Set when this session was created as a child of another (a consult). */
  parent?: string | null;
  status: 'active' | 'archived';
  title: string;
  /** The session's English internal record, rewritten at each turn's end. */
  summary: string;
  created_by: ActorRef;
  created_at: string;
  updated_at: string;
  archived_at?: string | null;
};

export type SessionStatus = SessionResponse['status'];

/**
 * One record of a session. A session holds ANY kind of data in one log — the
 * type names the payload's schema from an open vocabulary; the conversation
 * type is `com.ai4nr.session.message.conversation`, whose payload is the
 * ordered ContentBlock array.
 */
export type MessageResponse = {
  id: string;
  session: string;
  /** Per-session sequence — turn events name it, catch-up re-reads after it. */
  seq: number;
  type: string;
  role?: 'user' | 'agent' | null;
  author: ActorRef;
  /** The record's data in the type's own schema — opaque to the session service. */
  payload: unknown;
  created_at: string;
};

export const CONVERSATION_MESSAGE_TYPE = 'com.ai4nr.session.message.conversation';
export const TURN_FAILURE_MESSAGE_TYPE = 'com.ai4nr.session.message.turn_failure';

/** Why a turn failed: one kind per remedy (tricorder-session/ADR-0019). */
export type TurnFailureKind = (typeof TURN_FAILURE_KINDS)[number];

/** The payload of a com.ai4nr.session.message.turn_failure record: what went
 *  wrong and the options that resolve it, both written in `language` for the
 *  person whose turn failed. */
export type TurnFailurePayload = z.infer<typeof TurnFailurePayloadSchema>;

/** The record's payload when it holds the contract's shape, or null when it
 *  does not — a record the client cannot read is shown as unreadable. */
export function turnFailurePayload(payload: unknown): TurnFailurePayload | null {
  const parsed = TurnFailurePayloadSchema.safeParse(payload);
  return parsed.success ? parsed.data : null;
}

/** What the client itself can do about a failure kind, if anything. A kind a
 *  retry cannot change (the version, the account, the access) offers no
 *  retry. */
export type TurnFailureAction = 'retry' | 'new_conversation';

export function turnFailureAction(kind: TurnFailureKind): TurnFailureAction | null {
  switch (kind) {
    case 'run_failed':
    case 'timed_out':
    case 'tool_unreachable':
    case 'quota_exceeded':
    case 'no_answer':
    case 'tool_loop_aborted':
      return 'retry';
    case 'agent_unavailable':
      return 'new_conversation';
    case 'request_blocked':
    case 'account_unknown':
    case 'subject_unknown':
    case 'access_refused':
    case 'unreadable':
      return null;
  }
}

/** A protocol run's state change, as tricorder-protocol's `:step` answers it
 *  and the run screen appends it (tricorder-protocol SPEC section 7.1). The
 *  first names the version; the rest name the edge taken. A run's state is
 *  the fold of their state tokens. */
export const PROTOCOL_STEP_MESSAGE_TYPE = 'com.ai4nr.protocol.step';
/** A nurse's action on a protocol run, posted to the run's session. */
export const PROTOCOL_INPUT_MESSAGE_TYPE = 'com.ai4nr.protocol.input';

/** An observation value captured by an action (tricorder-protos
 *  schemas/protocol, `#Observation`). */
export type ProtocolObservation = Record<string, number | string | boolean>;

/** The payload of a com.ai4nr.protocol.step message (tricorder-protos
 *  schemas/protocol/step.cue). */
export type ProtocolStepPayload = {
  state_token: string;
  from_node_id?: string;
  to_node_id: string;
  action_id?: string;
  protocol_id?: string;
  protocol_version?: string;
  observation?: ProtocolObservation;
};

/** The payload of a com.ai4nr.protocol.input message (input.cue): the action
 *  taken, and the seq of the step message it was taken on. */
export type ProtocolInputPayload = {
  step_seq: number;
  node_id: string;
  action_id: string;
  observation?: ProtocolObservation;
};

/** One block of the conversation type's payload. */
export type ConversationBlock =
  | { type: 'text'; text: { text: string } }
  | { type: 'tool_call'; tool_call: { id: string; name: string; arguments: unknown } }
  | { type: 'tool_result'; tool_result: { tool_call_id: string; result: unknown } }
  | { type: 'citation'; citation: CitationResponse };

/**
 * isTurnReply reports whether a message ends a turn: its durable failure, or
 * the agent's conversation message that carries text and no tool call (a tool
 * call is a step of the turn, not its answer).
 */
export function isTurnReply(m: { type: string; role?: string | null; payload: unknown }): boolean {
  if (m.type === TURN_FAILURE_MESSAGE_TYPE) return true;
  if (m.type !== CONVERSATION_MESSAGE_TYPE || m.role !== 'agent' || !Array.isArray(m.payload)) return false;
  const blocks = m.payload as ConversationBlock[];
  return !blocks.some((b) => b?.type === 'tool_call') && blocks.some((b) => b?.type === 'text');
}

/** The text of a conversation message — its text blocks, in order. */
export function conversationText(payload: unknown): string {
  if (!Array.isArray(payload)) return '';
  return (payload as ConversationBlock[])
    .filter((b): b is Extract<ConversationBlock, { type: 'text' }> => b?.type === 'text')
    .map((b) => b.text.text)
    .join('\n\n');
}

/** The citations of a conversation message. */
export function conversationCitations(payload: unknown): CitationResponse[] {
  if (!Array.isArray(payload)) return [];
  return (payload as ConversationBlock[])
    .filter((b): b is Extract<ConversationBlock, { type: 'citation' }> => b?.type === 'citation')
    .map((b) => b.citation);
}

/**
 * Every list route's page (SPEC.md section 2): the body's `items`, and `next`,
 * the path the response's `Link: rel="next"` names, null on the last page.
 */
export type SessionPage<T> = Page<T>;

/** 202 of POST /sessions, a consult (a parent named) included. */
export type CreateSessionResponse = {
  session: SessionResponse;
  first_message: MessageResponse;
};

/** GET /sessions/{id}/summary. */
export type SessionSummaryResponse = {
  session: string;
  summary: string;
};

/** GET /sessions/{id}/prompt. */
export type PromptPreviewResponse = {
  agent: string;
  version: string;
  rendered: string;
  layers: { name: string; version: string }[];
};

/** One turn's prompt record (GET /sessions/{id}/prompt-records). */
export type PromptRecordResponse = {
  id: string;
  session: string;
  seq: number;
  author: ActorRef;
  rendered: string;
  layers: { name: string; version: string }[];
  created_at: string;
};

/** One trace row (GET /sessions/{id}/traces). */
export type SessionTraceResponse = {
  id: string;
  session: string;
  seq: number;
  kind: string;
  detail: unknown;
  latency_ms?: number | null;
  created_at: string;
};

/** One feedback entry (…/messages/{message_id}/feedback). */
export type SessionFeedbackResponse = {
  id: string;
  session: string;
  message: string;
  author: ActorRef;
  type: string;
  text?: string;
  created_at: string;
};

/** One elicitation (GET /sessions/{id}/elicitations). */
export type SessionElicitationResponse = {
  id: string;
  session: string;
  seq: number;
  tool_call: unknown;
  state: string;
  answer?: unknown;
  created_at: string;
  answered_at?: string | null;
};

/** One share grant (GET /sessions/{id}/shares — `{items}` only, no page). */
export type SessionShareResponse = {
  principal_type: string;
  principal_id: string;
  role: string;
  expires_at?: string | null;
};

/** Body of POST /sessions/{id}/elicitations. */
export type ResolveElicitationRequest = {
  elicitation_id: string;
  action: 'accept' | 'decline' | 'cancel';
  content?: unknown;
};

/** The SSE frames of GET /sessions/{id}/events. */
export type SessionEventFrame = {
  type: string;
  session_id?: string;
  seq?: number;
  payload?: unknown;
};

